import { type ComponentType, createElement, type ReactNode, useEffect, useState } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { LoadingState } from '../State';
import type { GeoLayer, LngLat } from './geo';
import s from './Map.module.css';

export type { GeoLayer, GeoLine, GeoLineLayer, GeoPoint, GeoPointLayer, GeoShape, GeoShapeLayer, GeoTone, LngLat } from './geo';
export { boundsOf, circleRing, distanceM, nearestIndex } from './geo';

/** Drawing a geofence: a circle (centre by click or drag, radius from the form) or a polygon. */
export type GeoDraw =
  | { mode: 'circle'; center: LngLat | null; radius_m: number; onCenterChange: (center: LngLat) => void; color?: string | null }
  | { mode: 'polygon'; points: LngLat[]; onPointsChange: (points: LngLat[]) => void; color?: string | null };

export interface GeoFocus {
  lng: number;
  lat: number;
  zoom?: number;
  /** Fly there whenever the key changes (e.g. the selected unit's id). */
  key: string;
  /** Keep the point in view as it moves (live units), without changing the zoom. */
  follow?: boolean;
  /** Pixels covered by an overlay on the right (a floating panel): centre in what stays visible. */
  coveredRight?: number;
}

export interface GeoMapProps {
  /** Accessible description of what the map shows. */
  label: string;
  /** Data layers, drawn in order (later on top). Sources are updated in place, never rebuilt. */
  layers: GeoLayer[];
  center?: LngLat;
  zoom?: number;
  /** Fit the view to every feature once per distinct key (e.g. once on load, again per selection). */
  fitKey?: string | number | null;
  fitMaxZoom?: number;
  focus?: GeoFocus | null;
  /** A feature of an interactive layer was clicked (cluster clicks zoom in instead). */
  onFeatureClick?: (layerId: string, featureId: string) => void;
  /** The map itself was clicked (not a feature), e.g. to pick a point. */
  onMapClick?: (lngLat: LngLat) => void;
  draw?: GeoDraw | null;
  /** Offer a full-screen toggle. */
  fullscreen?: boolean;
  height?: 'sm' | 'md' | 'lg' | 'fill';
  className?: string;
  /** Overlays positioned by the caller over the map (legends, status). */
  children?: ReactNode;
}

type Inner = ComponentType<Omit<GeoMapProps, 'height' | 'className' | 'children'>>;
let loaded: Inner | null = null;
let loading: Promise<Inner> | null = null;

/** MapLibre (about 1 MB) loads on first use. Not React.lazy: a Suspense retry for a map beside a
 * long table could stay parked behind the route transition and never show the map. */
function useInner(): Inner | null {
  // `() => loaded`, never `loaded`: React calls a function passed to useState as its initialiser,
  // which would render the map component with no props (the second map of a session crashed).
  const [inner, setInner] = useState<Inner | null>(() => loaded);
  useEffect(() => {
    if (inner) return;
    let alive = true;
    loading ??= import('./GeoMapInner').then((m) => (loaded = m.default));
    void loading.then((c) => alive && setInner(() => c));
    return () => {
      alive = false;
    };
  }, [inner]);
  return inner;
}

export function GeoMap({ height = 'md', className, children, ...props }: GeoMapProps) {
  const Inner = useInner();
  return (
    <div className={cx(s.frame, s[height], className)} translate="no">
      {Inner ? createElement(Inner, props) : <LoadingState label={t('Loading map…')} className={s.loading} />}
      {children}
    </div>
  );
}
